import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ArrowLeft, Database, Table2, ChevronRight, Loader2 } from 'lucide-react';
import { inspectionCategories } from './tableConfigs';
import { TableInspectorView } from './TableInspectorView';
import { adminDataInspectionService } from '../../../../services/adminDataInspectionService';
import { AdminInspectionSummaryDto, InspectionTableConfig } from '../../../../types/adminDataInspection';

interface DataInspectorProps {
  onBack: () => void;
}

// Map table config name to summary DTO field name
const summaryKeyMap: Record<string, keyof AdminInspectionSummaryDto> = {
  orders: 'ordersCount',
  orderLines: 'orderLineCount',
  plists: 'pListCount',
  plistLines: 'pListLineCount',
  plistOrders: 'pListOrdersCount',
  stcReservations: 'stcReservationCount',
  porders: 'pOrdersCount',
  porderLines: 'pOrderLineCount',
  stcTransOrders: 'stcTransOrderCount',
  stcIntRelocations: 'stcIntRelocationCount',
  itemBins: 'itemBinsCount',
  stcTransactions: 'stcTransactionCount',
  stcTrans: 'stcTransCount',
  items: 'itemsCount',
  stcIntRelOrders: 'stcIntRelOrderCount',
  stcIntRelOrderLines: 'stcIntRelOrderLineCount',
};

// Lookup: tableName → { categoryIndex, table config }
const tableIndex: Record<string, { categoryIndex: number; table: InspectionTableConfig }> = {};
inspectionCategories.forEach((cat, catIdx) => {
  cat.tables.forEach(table => {
    tableIndex[table.tableName] = { categoryIndex: catIdx, table };
  });
});

// Read deep-link state from URL params (Ctrl+click new tab)
// Auth initializes synchronously so URL params survive without redirect
function getInitialStateFromUrl(): { categoryIndex: number; table: InspectionTableConfig; filter: { key: string; value: string } | null } | null {
  const params = new URLSearchParams(window.location.search);
  const tableName = params.get('table');
  const filterKey = params.get('filterKey');
  const filterValue = params.get('filterValue');

  if (tableName && tableIndex[tableName]) {
    const target = tableIndex[tableName];
    const filter = filterKey && filterValue ? { key: filterKey, value: filterValue } : null;
    return { categoryIndex: target.categoryIndex, table: target.table, filter };
  }
  return null;
}

export const DataInspector: React.FC<DataInspectorProps> = ({ onBack }) => {
  const urlState = useMemo(() => getInitialStateFromUrl(), []);
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(urlState?.categoryIndex ?? 0);
  const [activeTable, setActiveTable] = useState<InspectionTableConfig | null>(urlState?.table ?? null);
  const [initialFilter, setInitialFilter] = useState<{ key: string; value: string } | null>(urlState?.filter ?? null);
  const [summary, setSummary] = useState<AdminInspectionSummaryDto | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  // Load summary on mount
  useEffect(() => {
    const loadSummary = async () => {
      try {
        const data = await adminDataInspectionService.getSummary();
        setSummary(data);
      } catch (err) {
        console.error('Failed to load summary:', err);
      } finally {
        setSummaryLoading(false);
      }
    };
    loadSummary();
  }, []);

  // Cross-table navigation handler — zero extra API calls, just switches table + sets filter
  const handleNavigateToTable = useCallback((targetTable: string, filterKey: string, filterValue: string) => {
    const target = tableIndex[targetTable];
    if (!target) return;
    setActiveCategoryIndex(target.categoryIndex);
    setInitialFilter({ key: filterKey, value: filterValue });
    setActiveTable(target.table);
  }, []);

  const activeCategory = inspectionCategories[activeCategoryIndex];

  const getRowCount = (tableName: string): string => {
    if (summaryLoading) return '...';
    if (!summary) return '?';
    const key = summaryKeyMap[tableName];
    if (key && summary[key] !== undefined) {
      return (summary[key] as number).toLocaleString('nb-NO');
    }
    return '?';
  };

  const handleBackFromTable = useCallback(() => {
    setActiveTable(null);
    setInitialFilter(null);
  }, []);

  const handleCategoryChange = useCallback((idx: number) => {
    setActiveCategoryIndex(idx);
    setActiveTable(null);
    setInitialFilter(null);
  }, []);

  return (
    <div className="flex flex-col h-full">
      {/* Header — always visible */}
      <div className="flex items-center gap-3 mt-2 mb-4 px-4">
        <button
          onClick={activeTable ? handleBackFromTable : onBack}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors shrink-0"
        >
          <ArrowLeft className="w-4 h-4" />
          Tilbake
        </button>
        <div className="flex items-center gap-2 shrink-0">
          <Database className="w-5 h-5 text-indigo-600" />
          <h1 className="text-lg font-semibold whitespace-nowrap">Data-inspeksjon</h1>
        </div>

        {/* Category tabs */}
        <div className="flex gap-1 ml-4">
          {inspectionCategories.map((cat, idx) => (
            <button
              key={cat.id}
              onClick={() => handleCategoryChange(idx)}
              className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                idx === activeCategoryIndex
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table breadcrumb + filter (when viewing a table) */}
      {activeTable && (
        <div className="flex items-center gap-2 mb-3 px-4 text-sm">
          <div className="flex items-center gap-2 text-gray-500">
            <span>{activeCategory.label}</span>
            <ChevronRight className="w-3 h-3" />
            <span className="font-medium text-gray-900">{activeTable.label}</span>
          </div>
          {initialFilter && (
            <div className="flex items-center gap-1 text-xs bg-indigo-50 text-indigo-700 px-2 py-1 rounded">
              <span>{initialFilter.key}: {initialFilter.value}</span>
              <button
                onClick={() => setInitialFilter(null)}
                className="ml-1 text-indigo-400 hover:text-indigo-600"
                title="Fjern filter"
              >
                ×
              </button>
            </div>
          )}
          <div className="ml-auto text-xs text-gray-400">
            {getRowCount(activeTable.tableName)} rader totalt
          </div>
        </div>
      )}

      {/* Content: table view or table cards */}
      {activeTable ? (
        <TableInspectorView
          key={`${activeTable.tableName}-${initialFilter?.key}-${initialFilter?.value}`}
          tableConfig={activeTable}
          onNavigateToTable={handleNavigateToTable}
          initialFilter={initialFilter ?? undefined}
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 px-4">
          {activeCategory.tables.map(table => (
            <button
              key={table.tableName}
              onClick={() => setActiveTable(table)}
              className="flex flex-col p-4 bg-white border border-gray-200 rounded-lg hover:border-indigo-300 hover:shadow-md transition-all text-left group"
            >
              <div className="flex items-center gap-2 mb-2">
                <Table2 className="w-4 h-4 text-indigo-500 group-hover:text-indigo-600" />
                <span className="font-medium text-gray-900">{table.label}</span>
              </div>
              <p className="text-xs text-gray-500 mb-3">{table.description}</p>
              <div className="mt-auto flex items-center justify-between">
                <span className="text-xs text-gray-400">{table.columns.length} kolonner</span>
                <span className="text-sm font-mono text-indigo-600">
                  {summaryLoading ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    getRowCount(table.tableName)
                  )}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
